/*
 * A wrapper containing balance, available balance, and float widgets.
 * This is intended for use in the reseller dashboard, and in the reseller budget tab
 * This contains an optional float limit increase request.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment'; 
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Link, TextField, Tooltip, Typography } from '@mui/material';
import AvailableBalanceTextWidget from './AvailableBalanceTextWidget';
import TextWidget from '../ui/TextWidget';
import LinearProgressWidget from '../ui/LinearProgressWidget';
import CurrencyTextField from '../ui/CurrencyTextField';
import CreditCard from '@mui/icons-material/CreditCard';

import LimitRequestDialog from './LimitRequestDialog';
import AppBindStyles from '../../AppBindStyles';
import PaymentRailsAccountFetch from './PaymentRailsAccountFetch';
import OpenPayoutFetch from './OpenPayoutFetch';
import InitiatePayoutDialog from './InitiatePayoutDialog';
import LoadingSpinner from '../ui/LoadingSpinner';
import { format } from 'date-fns';
import { getCurrencyString } from '../../utils/InputUtils';

const abEnvironment = new AbEnvironment();
const BalanceAndFloatWidgetsQuery = graphql`
query BalanceAndFloatWidgetsQuery($accountId: UUID!) {
  getaccountledgersummary(accountId: $accountId, balancedate: null) {
    assetBalance
    assetAndArBalance
    assetArAuthBalance
    equityBalance
    maxPayoutBalance
    liabilityBalance
    authBalance
  }
  getunreceivedchargedetail(accountid: $accountId) { 
    totalCount nodes { 
      transactionId transactionType transactionTime transDescription extSource extReferral extTransactionId changeType changeAmount 
    } 
  }
  accountTByAccountId(accountId: $accountId) {
    achPercAvailableOnCharge 
    achMinAvailableOnCharge
    ccPercAvailableOnCharge
    ccMinAvailableOnCharge
  }
}
`;

const styleSheet = theme => ({
  root: {
    width: '100%',
  },
  buttonicon: {
    marginRight: 6,
  },
  instructButton: {
    marginTop: -1,
  },
  widgetContainer: {
    // marginTop: 8,
    //marginBottom: 8,
  }
});
  
/*
 * Props coming in:
 * - narrow           (use narrower tiles)
 * - auth
 * - showEditLink     (show text describing settings, and a link to the budget tab to change the settings)
 * - payFromBalance
 * - autoTopupAmount    (Number)
 * - autoTopupThreshold (Number)
 * - showLimitRequest (show the button / dialog for requesting an increase)
 * - floatLimit       (limit on the account)
 * - accountId        (for lookup)
 * - useBudgets       (what to call the float)
 * - history
 */
class BalanceAndFloatWidgets extends Component {
    
  constructor(props) {
    super(props);

    this.state = {
      currentFloatLimit: 0,
      floatLimit: 0,
      showLimitRequestDialog: false,
      limitRequestReason: '',
      existingPaymentRailsAccount: false,
      showPayoutDialog: false,
      payoutAmount: 0,
      executingPayout: false,
      forceFetch: 0,
      existingPayout: false,
      existingPayoutAmount: null,
      existingPayoutDate: null,
    };  
  }

  componentWillUnmount() {
    this.mounted = false; // Antipattern workaround
  }

  componentDidMount() {
    this.mounted = true;

    //see if the account has a payment rails account set up in good standing.
    let accountId = abEnvironment.getUserAttribute('a_id');
    PaymentRailsAccountFetch.fetch(accountId).toPromise()
    .then(praf => {
      
      if (praf.allPaymentSourceTs.totalCount > 0) {
        if (praf.allPaymentSourceTs.nodes[0].psStatusIn === 0) {
          //console.log('We have an exsting PR account in good status');
          if (this.mounted) {
            this.setState({existingPaymentRailsAccount: true})
          }
        }
      }
  
    })
    .catch(err => {
      //nothing to do....
    })

    OpenPayoutFetch.fetch(accountId).toPromise()
    .then(opf => {
      //console.log(JSON.stringify(opf));
      if (opf.lookupopenpayout.payoutOpen === 1) {
        if (this.mounted) {
          this.setState({existingPayout: true, existingPayoutAmount: opf.lookupopenpayout.payoutAmount, existingPayoutDate: opf.lookupopenpayout.requestedDate});
        }
      } else {
        if (this.mounted) {
          this.setState({existingPayout: false});
        }
      }
  
    })
    .catch(err => {
      //nothing to do....
    })

  }

  showLimitRequestDialog = (floatLimit) => {
    let setFloatLimit = this.props.floatLimit;
    if (this.state.floatLimit > 0) {
      setFloatLimit = this.state.floatLimit;
    }
    this.setState({
      showLimitRequestDialog: true,
      currentFloatLimit: floatLimit,
      floatLimit: setFloatLimit
    });
  }

  determinePayoutNextStep = (accountId) => {
    if (this.state.existingPaymentRailsAccount) {
      this.initiatePayout(accountId);
    } else {
      this.goTo('fundingsources', 2)
    }
  }

  showPayoutRequestDialog = () => {
    
    //console.log('showing existing payment rails account');
    this.setState({ showPayoutDialog: true });
  }

  initiatePayout(accountId) {
    const { callPartnerApi } = this.props.auth;

    let forceFetch = this.state.forceFetch;
    forceFetch++;

    let stringAmt = Number(this.state.payoutAmount).toString()

    this.setState({executingPayout: true});

    let aPjwt = abEnvironment.getPjwt();
    let atId  = 'f5c70f86-135a-11e8-bb45-97ef56920c02';
    let today = new Date();

    let payload = {
      operation: "initiatePayout",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        atId: atId,
        payoutAmount: stringAmt,
        coverFees: true //TODO follow up with Joe.
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.status !== 0) {
        let theErrorText = "Unidentified Error";

        if (abpResponse != null && abpResponse.message != null) {
          theErrorText = abpResponse.message;
        }
        this.setState({ executingPayout: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
        return;
      }

      this.setState({ executingPayout: false, existingPayout: true, existingPayoutAmount: this.state.payoutAmount, existingPayoutDate: today, forceFetch: forceFetch, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Payout sent', saveSnackbarDuration: 3000 });
    })
    .then(response => {

      let theBody = abEnvironment.getUserAttribute('a_accountName') + ' has initiated a payout.'
      let payload = {
        operation: "createNotification",
        params: {
          pJwt:             aPjwt,
          accountId:        this.props.accountId,
          notificationType: 'payout',
          severity:         'info',
          subject:          'Payout has been initiated',
          body:             theBody
        }
      }
      
      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
  
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ executingPayout: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
    });
  }

  /**
   * disabled initiate payout in case of no payment account, payout less than $1,
   *   or amount is > maxPayout minus the min balance prior to top-up (if exists.)
   * @param {Number} maxPayoutBalance max payout balance (from the query)
   * @returns True if button disable, false if enabled
   */
  disableInitiatePayout(maxPayoutBalance) {

    if (this.state.existingPaymentRailsAccount === false)
      return true;

    if (this.state.payoutAmount < 1)
      return true;

    // the max for paying out is the minimum between the maxPayoutBalance value and the difference between the full balance and auto-topup threshold.
    let autoTopupAmount = this.props.autoTopupAmount ? this.props.autoTopupAmount : 0;
    let maxPayout = maxPayoutBalance - autoTopupAmount;
    
    if (this.state.payoutAmount > maxPayout)
      return true;
    
    return false;
  }

  sendLimitIncreaseRequest(accountId) {
    // For now, notification
    const { callPartnerApi } = this.props.auth;
    if (abEnvironment.isSegmentActive()) { window.analytics.track('Limit Increase Request'); }

    let theBody = 'You requested';

    if (this.state.floatLimit !== this.state.currentFloatLimit) {
      let reqFloat = Number(this.state.floatLimit).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
      theBody = theBody + ' an outstanding balance limit of ' + reqFloat;

      let aPjwt = abEnvironment.getPjwt();
      theBody = theBody + ' with this reason: ' + this.state.limitRequestReason;

      let payload = {
        operation: "createNotification",
        params: {
          pJwt:             aPjwt,
          accountId:        accountId,
          notificationType: 'increase',
          severity:         'info',
          subject:          'Limit increase request',
          body:             theBody
        }
      }
      
      callPartnerApi('appbind/generic',
                    'POST',
                    payload
      )
      .then(response => {
        this.setState({showLimitRequestDialog: false, limitRequestReason: ''});
      })
      .catch(error => {
        this.setState({showLimitRequestDialog: false, limitRequestReason: ''});
      });
    }
  }

  goTo(route, theTab) {
    if (theTab != null) {
      localStorage.setItem('psInOut', theTab);
    }
    this.props.history.push(`/${route}`);
  }

  render() {
    const classes = this.props.classes;

    const { isAuthenticatedReseller } = this.props.auth;

    let showTopUpStuff = false;
    if (this.props.useBudgets >= 1) {
      showTopUpStuff = true;
    }

    let autoTopupAmountString = getCurrencyString(this.props.autoTopupAmount);
    let autoTopupThresholdString = getCurrencyString(this.props.autoTopupThreshold);

    let existingPayoutAmount = Number(this.state.existingPayoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    let existingPayoutDate = format(new Date(this.state.existingPayoutDate), 'yyyy-MM-dd');
      
    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={BalanceAndFloatWidgetsQuery}
        variables={{accountId: this.props.accountId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {

          if (error) {
            return <div>{error.message}</div>
          } else if (props) {
            // For balance widget
            let eb = props.getaccountledgersummary.equityBalance;
            let equityBalance = Number(eb).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            // For available balance widget
            let ab = props.getaccountledgersummary.assetArAuthBalance;
            // Don't show an available balance greater than the equity balance.
            if (Number(ab) > Number(eb)) {
              ab = eb;
              // If equity balance is negative, just show 0 for available.
              if (Number(ab) < 0) {
                ab = 0;
              }
            }

            let availableBalance = Number(ab).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            abEnvironment.setUserAttribute("a_availableBalance", availableBalance);

            let payoutAmountString = Number(this.state.payoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            let maxPayoutBalance = Number(props.getaccountledgersummary.maxPayoutBalance);
            let mpb = maxPayoutBalance * 1000;
            let maxPayoutBalanceDisplay = maxPayoutBalance.toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            //the max for paying out is the minimum between the maxPayoutBalance value and the difference between the full balance and auto-topup threshold.
            let autoTopupAmount = this.props.autoTopupAmount ? this.props.autoTopupAmount : 0;
            let maxPayoutCheckValue = maxPayoutBalance - autoTopupAmount;

            // Float widget
            let floatLimit = this.props.floatLimit;

            // Derive the float balance from equity balance and outstanding auth.
            let authBalance = Number(props.getaccountledgersummary.authBalance);
            let floatBalance = (Number(eb) * -1) + Number(authBalance);
            if (floatBalance < 0) {
              floatBalance = 0;
            }

            // There are 3 use cases we are supporting:
            // 1. Reseller looking at own account (on My funding)
            // 2. Customer looking at own account (on My funding)
            // 3. Reseller looking at customer account
            //
            // In addition, during the transition to wallets, in the case that topups/budgets are enabled, but
            // wallet conversion is not, we want to talk about account balance using wallet verbiage.
            //
            // Depending on which case it is, there will be different verbiage in the various chicklets
            //

            // 1. Set up defaults for the reseller case.
            let balanceTitle = "Your balance";
            let balanceTt = "If you markup SaaS apps, bill your services through AppBind, or receive rebates from vendors, you will build a balance in AppBind. You can also add funds to your balance from a funding source.";
            let availableBalanceTitle = "Your available balance";
            let availableBalanceTt = "Your balance may include funds that haven't settled yet, so this indicates what is currently available to pay for subscriptions.";
            let floatLimitTitle1 = "Float (";
            let floatLimitTitle2 = "% of limit)";
            let floatLimitTt = "We provide a float for purchasing subscriptions and services.  This, plus your available balance, is the amount available for purchases.  Each individual charge typically clears within a few days, but if you provision a large number of services you may need a higher limit."

            // Dialog titles.
            let floatCreateTitle = "Request float";
            let floatIncreaseTitle = "Request a float increase";
            let floatCreateMessage = "We set a limit on your float balance, and decline charges that exceed this limit.  To request a higher limit, please indicate your desired float limit, and your reason for requesting an increase.";
            let floatIncreaseMessage = "We set a limit on your float balance, and decline charges that exceed this limit.  To request a higher limit, please indicate your desired float limit, and your reason for requesting an increase.";

            if (showTopUpStuff) {
              balanceTitle = "Wallet balance";
              balanceTt = "If you markup SaaS apps, bill your services through AppBind, or receive rebates from vendors, you will build a balance in your AppBind wallet. You can also add funds to your wallet balance from a funding source.";
              availableBalanceTitle = "Wallet available balance";
              availableBalanceTt = "Your balance may include funds that haven't settled yet, so this indicates what is currently available to pay for subscriptions.";
  
              floatLimitTitle1 = "Overdraft (";
              floatLimitTt = "We may provide an overdraft float for purchasing subscriptions and services.  This, plus your available balance, is the amount available for purchases.  Each individual charge typically clears within a few days, but if you provision a large number of services you may need a higher limit."

              floatCreateTitle = "Request overdraft";
              floatIncreaseTitle = "Request an overdraft increase";
              floatCreateMessage = "We decline charges that exceed your wallet balance.  Optionally, we provide an overdraft float that will allow charges to exceed your balance.  If you want to request an overdraft float, please indicate the size of the overdraft float you would like, and your reason for requesting an overdraft float.";
              floatIncreaseMessage = "We're providing you with an overdraft float that allows charges to exceed your wallet available balance.  To request an overdraft float increase, please indicate the size of the overdraft float you would like, and and your reason for requesting an increase.";
            }

            //max payout widget text
            let maxPayoutBalanceTitle = "Available to pay out";
            let maxPayoutBalanceTt = "You can only pay out funds that have finished clearing into your balance. If you automatically add funds to your balance, you won't be able to pay out an amount that would trigger automated funding.";

            if (abEnvironment.getUserAttribute("a_aType") === "4") {

              // 2. Customer looking at own account.  Override here as necessary.
              balanceTitle = "Your balance";
              balanceTt = "When you add funds to your account, you'll have a positive balance in AppBind that can be used to pay for subscriptions.";
              availableBalanceTitle = "Your available balance";
              availableBalanceTt = "Your balance may include funds that haven't settled yet, so this indicates what is currently available to pay for subscriptions.";

              if (showTopUpStuff) {
                balanceTitle = "Wallet balance";
                balanceTt = "When you add funds to your wallet, you'll have a positive balance that can be used to pay for subscriptions.";
                availableBalanceTitle = "Wallet available balance";
                availableBalanceTt = "Your wallet balance may include funds that haven't settled yet, so this indicates what is currently available to pay for subscriptions.";

                floatLimitTitle1 = "Overdraft (";
                floatLimitTt = "We may provide an overdraft float for purchasing subscriptions and services.  This, plus your available balance, is the amount available for purchases.  Each individual charge typically clears within a few days, but if you provision a large number of services you may need a higher limit."

                floatCreateTitle = "Request overdraft";
                floatIncreaseTitle = "Request an overdraft increase";
                floatCreateMessage = "We decline charges that exceed your balance in AppBind.  Optionally, we provide an overdraft float that will allow charges to exceed your balance.  If you want to request an overdraft float, please indicate the size of the overdraft float you would like, and your reason for requesting an overdraft float.";
                floatIncreaseMessage = "We're providing you with an overdraft float that allows charges to exceed your available balance.  To request an overdraft float increase, please indicate the size of the overdraft float you would like, and and your reason for requesting an increase.";
              }

            } else if (this.props.accountId !== abEnvironment.getUserAttribute("a_id")) {
              // Set the reseller view customer messages
              balanceTitle = "Balance";
              balanceTt = "When your customer adds funds to their account, they'll have a positive balance in AppBind.";
              availableBalanceTitle = "Available balance";
              availableBalanceTt = "Your customer's balance may include funds that haven't settled yet, so the available balance indicates what is currently available for purchases.";
              floatLimitTt = "We may provide your customer with a float for purchasing subscriptions and services.  This, plus your customer's available balance, is the amount available for purchases.  Each individual charge typically clears within a few days, but if you provision a large number of services, your customer may need a higher limit."

              if (showTopUpStuff) {
                balanceTitle = "Wallet balance";
                balanceTt = "When your customer adds funds to their wallet, they'll have a positive balance that can be used to pay for subscriptions.";
                availableBalanceTitle = "Wallet available balance";
                availableBalanceTt = "Your customer's wallet balance may include funds that haven't settled yet, so this indicates what is currently available to pay for subscriptions.";

                floatLimitTitle1 = "Overdraft (";
                floatLimitTt = "We may provide your customer with an overdraft float for purchasing subscriptions and services.  This, plus your customer's available balance, is the amount available for purchases.  Each individual charge typically clears within a few days, but if you provision a large number of services, your customer may need a higher limit."

                floatCreateTitle = "Request overdraft";
                floatIncreaseTitle = "Request an overdraft increase";
                floatCreateMessage = "We decline charges that exceed your customer's available balance in AppBind.  Optionally, we provide an overdraft float that will allow charges to exceed your customer's available balance.  If you want to request an overdraft float for your customer, please indicate the size of the overdraft float you would like, and your reason for requesting an overdraft float.";
                floatIncreaseMessage = "We're providing your customer with an overdraft float that allows charges to exceed your customer's available balance.  To request an overdraft float increase for your customer, please indicate the size of the overdraft float you would like, and and your reason for requesting an increase.";
              }
            }

            let theDialogTitle = floatCreateTitle;
            let theDialogMessage = floatCreateMessage;
            if (this.props.floatLimit > 0) {
              theDialogTitle = floatIncreaseTitle;
              theDialogMessage = floatIncreaseMessage;
            }

            if (this.state.executingPayout) {
              return (<LoadingSpinner />);
            } else {
              return (
                <React.Fragment>
                  <Grid container spacing={3} justifyContent="flex-start" className={classes.widgetContainer}>
                    <Grid item>
                      <TextWidget
                        narrow={this.props.narrow}
                        value={equityBalance}
                        description={balanceTitle}
                        tooltip={balanceTt}
                      />
                    </Grid>
                    <Grid item>
                      {/*<TextWidget
                        narrow={this.props.narrow}
                        value={availableBalance}
                        description={availableBalanceTitle}
                        tooltip={availableBalanceTt}
                      />*/}

                      <AvailableBalanceTextWidget
                        narrow={this.props.narrow}
                        value={availableBalance}
                        description={availableBalanceTitle}
                        tooltip={availableBalanceTt}
                        pendingReceipts={props.getunreceivedchargedetail.nodes}
                        accountPercentages={props.accountTByAccountId}
                      />
                    </Grid>
                    {/* Max payout currently not intended for customer role view (other roles will show it). */}
                    {(this.props.accountType === 3 || this.props.accountType === '3') &&
                    <Grid item>
                      <TextWidget
                        narrow={this.props.narrow}
                        value={maxPayoutBalanceDisplay}
                        description={maxPayoutBalanceTitle}
                        tooltip={maxPayoutBalanceTt}
                      />
                    </Grid>
                    }
                    <Grid item>
                      <LinearProgressWidget
                        narrow={this.props.narrow}
                        nominator={floatBalance}
                        denominator={floatLimit}
                        descriptionStart={floatLimitTitle1}
                        descriptionEnd={floatLimitTitle2}
                        tooltip={floatLimitTt}
                      />
                    </Grid>
                    {this.props.showLimitRequest &&
                      <Grid item>
                        <Button color="primary"  variant="outlined" size="small" 
                                onClick={() => {this.showLimitRequestDialog()}}>
                          Request higher limits
                        </Button>
                      </Grid>
                    }
                    <Grid item>
                      {((maxPayoutBalance <= 0) && isAuthenticatedReseller()) &&
                        <Tooltip title={'You do not have a balance available to pay out.'}>
                          <Button color="primary"  variant="outlined" size="small" disabled={false}>
                            Pay out balance
                          </Button>
                        </Tooltip>
                      }
                      {((maxPayoutBalance > 0) && isAuthenticatedReseller() && this.state.existingPayout === true) &&
                        <Tooltip title={"A payout of " + existingPayoutAmount + ' was initiated on ' + existingPayoutDate + ' and is in process.'}>
                          <Button color="primary"  variant="outlined" size="small" disabled={false}>
                            Pay out balance
                          </Button>
                        </Tooltip>
                      }
                      {((maxPayoutBalance > 0) && isAuthenticatedReseller() && this.state.existingPayout === false) &&
                        <Button color="primary"  variant="outlined" size="small" 
                                onClick={() => {this.showPayoutRequestDialog()}}>
                          Pay out balance
                        </Button>
                      }
                    </Grid>
                    {this.props.showEditLink &&
                    <Grid item xs={12}>
                      {(this.props.payFromBalance == null || this.props.payFromBalance === 0) &&
                        <Typography variant="body2" style={{ lineHeight: "2" }}>
                          <span>These subscriptions will be automatically charged to your funding sources as set in</span>&nbsp;&nbsp;
                          <Button color="primary" 
                            size="small" 
                            variant="outlined" 
                            className={classes.instructButton}
                            onClick={() => this.goTo('fundingsources', 1)}
                          >
                            <CreditCard className={classes.buttonicon}/> My Funding
                          </Button> 
                        </Typography>
                      }
  
                      {this.props.payFromBalance != null && this.props.payFromBalance !== 0 &&
                        <Typography variant="body2" style={{ lineHeight: "2" }}>
                          <span>Subscriptions will be charged to your available balance of</span>&nbsp;
                          <span style={AppBindStyles.boldLabel}>{availableBalance}</span>
                          {this.props.autoTopupAmount && this.props.autoTopupThreshold &&
                            <React.Fragment>
                              .&nbsp;&nbsp;
                              <span>Your balance will be topped up to</span>&nbsp;
                              <span style={AppBindStyles.boldLabel}>{autoTopupAmountString}</span>&nbsp;
                              <span>when it falls below</span>&nbsp;
                              <span style={AppBindStyles.boldLabel}>{autoTopupThresholdString}</span>
                            </React.Fragment>
                          }

                          &nbsp;<span>as set in</span>&nbsp;&nbsp;
                          <Button color="primary" 
                            size="small" 
                            variant="outlined" 
                            className={classes.instructButton}
                            onClick={() => this.goTo('fundingsources', 1)}
                          >
                            <CreditCard className={classes.buttonicon}/> My Funding
                          </Button>
                        </Typography>
                      }
                    </Grid>
                    }
                  </Grid>
  
                  <InitiatePayoutDialog
                    open={this.state.showPayoutDialog}
                    onClose={() => { this.setState({showPayoutDialog:false}); }}
                    onConfirm={() => { this.determinePayoutNextStep(this.props.accountId) }}
                    onCancel={() => { this.setState({showPayoutDialog:false}); }}
                    onEdit={() => { this.goTo('fundingsources', 2); }}  //note edit button went away and should be removed.
                    amount={this.state.payoutAmount}
                    title={'Pay out balance'}
                    cancelBtn="Cancel"
                    editBtn={false}
                    okBtn={(this.state.existingPaymentRailsAccount === true) ? "Pay out " + payoutAmountString + " now": "Setup payout method"}
                    okDisabled={this.disableInitiatePayout(maxPayoutBalance, ab)}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {this.state.existingPaymentRailsAccount === true ?
                          <React.Fragment>
                            <Typography variant="body1">You have up to {maxPayoutBalanceDisplay} to pay out to the</Typography>
                            <Link style={{marginTop: -2}} component="button" variant="body1" color="primary" onClick={() => {this.goTo('fundingsources', 2);}}>payout method you have on file.</Link>
                          </React.Fragment>
                        : //this.state.existingPaymentRailsAccount === false
                          <Typography variant="body1">{'In order to payout of your balance, you need to first set up a payout method.'}</Typography>
                        }
                      </Grid>

                      <Grid item xs={12}>
                        {this.state.existingPaymentRailsAccount === true &&
                        <CurrencyTextField 
                          id="payout-amount" label="Payout amount"
                          value={this.state.payoutAmount}
                          onChange={ (e) => this.setState({ payoutAmount: e.target.value }) }
                          showErrMsg={true}
                          allowNegative={false}
                          minValue={1}
                          minEnterValue={0}
                          maxValue={maxPayoutCheckValue}
                          maxEnterValue={mpb}
                          maxErrMsg={maxPayoutCheckValue === maxPayoutBalance ? null : `Payout amount can't leave balance below auto-topup amount (${autoTopupAmountString}).`}
                          textAlign="right"
                        />
                        }
                      </Grid>
                    </Grid>
                  </InitiatePayoutDialog>
  
                  <LimitRequestDialog
                    open={this.state.showLimitRequestDialog}
                    onClose={() => { this.setState({showLimitRequestDialog:false});}}
                    onConfirm={() => { this.sendLimitIncreaseRequest(this.props.accountId)}}
                    onCancel={() => { this.setState({showLimitRequestDialog:false});}}
                    reason={this.state.limitRequestReason}
                    title={theDialogTitle}
                    cancelBtn="Cancel"
                    okBtn="Send Request"
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1">{theDialogMessage}</Typography>
                      </Grid>
                      <Grid item>
                        <CurrencyTextField 
                          id={showTopUpStuff ? "balance-limit" : "float-limit"}
                          label={showTopUpStuff ? "Overdraft limit" : "Float limit"}
                          value={this.state.floatLimit}
                          onChange={ (e) => this.setState({ floatLimit: e.target.value }) }
                          showErrMsg={true}
                          allowNegative={false}
                          minValue={0}
                          minEnterValue={0}
                          textAlign="right"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          id="reason-text" label="Reason for increase" 
                          value={this.state.limitRequestReason}
                          fullWidth
                          multiline
                          onChange={(e) => this.setState({ limitRequestReason: e.target.value })}
                          inputProps={{ maxLength: 2048 }} />
                      </Grid>
                    </Grid>
                  </LimitRequestDialog>
                </React.Fragment>
              );
            }
          }            
        }}
      />
    );
  }
}

export default withStyles(styleSheet)(BalanceAndFloatWidgets);